import { useEffect, useId, useRef, useState } from "react";
import { canDocflow } from "@/auth/portal";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import {
  Archive,
  Check,
  Copy,
  FileSpreadsheet,
  Handshake,
  Link2,
  Loader2,
  Pencil,
  RotateCcw,
  Search,
  UserPlus,
  X,
} from "lucide-react";
import { toast } from "sonner";

import {
  applyImport,
  archivePartnerFn,
  inviteListedFn,
  invitePartner,
  previewImport,
  loadPartners,
  reissueInviteFn,
  restorePartnerFn,
  updatePartnerFn,
  type ImportPreview,
  type InviteResult,
  type PartnersView,
} from "@/braining/partners.functions";
import {
  INVITE_DAYS,
  KIND_LABEL,
  PARTNER_KINDS,
  STATUS_LABEL,
  type PartnerKind,
  type PartnerRow,
} from "@/braining/partners";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { REQ_STATE_LABEL } from "@/braining/partner-requisites";
import { cn } from "@/lib/utils";

/**
 * Подрядчики — экран того, кто ведёт документы.
 *
 * Встреча 14.10: «я завожу его в этом интерфейсе, и дальше подрядчику
 * отправляется в некоторый интерфейс, в своё окно, в котором он сам
 * заполняет все реквизиты». Здесь — первая половина: завести подрядчика и
 * выдать ему ссылку в кабинет. Почту система не отправляет, поэтому ссылку
 * пересылают сами — в Телеграм или письмом.
 *
 * Ведут администратор и руководитель; у менеджера пункта в меню нет, а
 * адрес уводит на калькуляторы. Сервер и RLS проверяют то же самое.
 */
export const Route = createFileRoute("/contractors")({
  head: () => ({ meta: [{ title: "Подрядчики — Портал Braining Group" }] }),
  beforeLoad: ({ context }) => {
    if (!canDocflow(context.user?.role)) throw redirect({ to: "/" });
  },
  component: ContractorsPage,
});

function ContractorsPage() {
  const [view, setView] = useState<PartnersView | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** последняя выданная ссылка — показываем, пока не скопировали и не ушли */
  const [link, setLink] = useState<{ id: string | null; name: string; url: string } | null>(null);
  const [query, setQuery] = useState("");
  const [fill, setFill] = useState<Fill | "all">("all");
  const q = query.trim().toLowerCase();
  const rows = view?.rows ?? [];
  const shown = rows.filter(
    (r) =>
      (fill === "all" || fillOf(r) === fill) &&
      (!q || [r.name, r.email ?? "", r.telegram ?? ""].some((v) => v.toLowerCase().includes(q))),
  );
  const fillCount = (f: Fill) => rows.filter((r) => fillOf(r) === f).length;

  async function refresh() {
    try {
      setView(await loadPartners());
    } catch (e) {
      console.error("[contractors] не удалось прочитать список", e);
      setError("Не удалось прочитать список подрядчиков");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  function onLink(res: InviteResult, id: string | null, name: string): boolean {
    if (!res.ok) {
      setError(res.error);
      return false;
    }
    setError(null);
    setLink({ id, name, url: res.url });
    return true;
  }

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Подрядчики</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Приглашайте подрядчиков в кабинет партнёра: там они сами заведут реквизиты, а потом будут
          согласовывать документы.
        </p>
      </div>

      {view && !view.base && (
        <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning-ink">
          Не задан адрес кабинета партнёров (PARTNERS_URL) — выдать ссылку некуда. Это настройка
          сервера, её делает разработчик.
        </p>
      )}

      {error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive-ink">
          {error}
        </p>
      )}

      {link && <LinkBox {...link} onClose={() => setLink(null)} />}

      <InviteForm
        disabled={!view?.base}
        onDone={async (res, name) => {
          if (onLink(res, null, name)) await refresh();
        }}
      />

      <ImportPanel onAdded={refresh} />

      <Card>
        <CardContent className="space-y-3 py-4">
          <div className="flex items-center gap-2">
            <Handshake className="size-4 text-muted-foreground" />
            <span className="text-sm font-medium">Кто есть</span>
            {view && <span className="text-xs text-muted-foreground">{view.rows.length}</span>}
            {view && view.rows.length > 8 && (
              <div className="relative ml-auto w-56">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
                <Input
                  value={query}
                  placeholder="Найти: имя, почта, ник"
                  className="h-9 pl-8"
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            )}
          </div>

          {view && view.rows.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {(["all", "full", "partial", "empty"] as const).map((f) => (
                <Button
                  key={f}
                  size="sm"
                  variant={fill === f ? "secondary" : "ghost"}
                  className="h-7 px-2.5 text-xs"
                  onClick={() => setFill(f)}
                >
                  {f === "all" ? "Все" : FILL_LABEL[f]}
                  <span className="text-muted-foreground">
                    {f === "all" ? rows.length : fillCount(f)}
                  </span>
                </Button>
              ))}
            </div>
          )}

          {view === null ? (
            <p className="text-sm text-muted-foreground">Загружаем…</p>
          ) : view.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Подрядчиков пока нет. Пригласите первого — форма выше.
            </p>
          ) : (
            <div className="divide-y rounded-md border">
              {shown.map((row) => (
                <PartnerLine
                  key={row.id}
                  row={row}
                  canLink={Boolean(view.base)}
                  onLink={(res) => onLink(res, row.id, row.name)}
                  onChanged={refresh}
                  onError={setError}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function InviteForm({
  disabled,
  onDone,
}: {
  disabled: boolean;
  onDone: (res: InviteResult, name: string) => Promise<void>;
}) {
  const uid = useId();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<PartnerKind>("self_employed");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name.trim() || !email.trim()) return;
    setBusy(true);
    try {
      const res = await invitePartner({ data: { name, kind, email } });
      await onDone(res, name.trim());
      if (res.ok) {
        setName("");
        setEmail("");
      }
    } catch (e) {
      console.error("[contractors] не удалось пригласить", e);
      await onDone({ ok: false, error: "Не удалось пригласить подрядчика" }, name);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="space-y-4 py-4">
        <div className="flex items-center gap-2">
          <UserPlus className="size-4 text-muted-foreground" />
          <span className="text-sm font-medium">Пригласить подрядчика</span>
        </div>

        <div className="grid gap-3 sm:grid-cols-[1fr_170px_1fr]">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-name`} className="text-xs font-normal text-muted-foreground">
              Как зовут
            </Label>
            <Input
              id={`${uid}-name`}
              value={name}
              placeholder="Иван Петров"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-normal text-muted-foreground">Оформление</Label>
            <KindSelect value={kind} onChange={setKind} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-email`} className="text-xs font-normal text-muted-foreground">
              Почта — для входа
            </Label>
            <Input
              id={`${uid}-email`}
              type="email"
              value={email}
              placeholder="ivan@example.com"
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => void submit()}
            disabled={disabled || busy || !name.trim() || !email.trim()}
          >
            {busy && <Loader2 className="size-4 animate-spin" />}
            Пригласить
          </Button>
          <span className="text-xs text-muted-foreground">
            Появится ссылка — перешлите её подрядчику сами. Живёт {INVITE_DAYS} дней и срабатывает
            один раз.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function KindSelect({
  value,
  onChange,
  className,
}: {
  value: PartnerKind;
  onChange: (v: PartnerKind) => void;
  className?: string;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as PartnerKind)}>
      <SelectTrigger className={cn("w-full", className)}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {PARTNER_KINDS.map((k) => (
          <SelectItem key={k} value={k}>
            {KIND_LABEL[k]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Ссылка, которую только что выдали: показать целиком и дать скопировать. */
function LinkBox({ name, url, onClose }: { name: string; url: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Ссылка скопирована");
    } catch {
      toast.error("Не удалось скопировать — выделите ссылку руками");
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-primary/30 bg-primary/5 px-4 py-3">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Link2 className="size-4" />
        Ссылка для «{name}»
        <button
          type="button"
          onClick={onClose}
          aria-label="Скрыть ссылку"
          className="ml-auto rounded p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="flex gap-2">
        <Input
          readOnly
          value={url}
          className="font-mono text-xs"
          onFocus={(e) => e.target.select()}
        />
        <Button variant="outline" onClick={() => void copy()}>
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          Скопировать
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Перешлите её подрядчику сами. Больше ссылку не показать: в базе хранится только её
        отпечаток. Потеряли — выдайте новую, старая погаснет.
      </p>
    </div>
  );
}

function PartnerLine({
  row,
  canLink,
  onLink,
  onChanged,
  onError,
}: {
  row: PartnerRow;
  canLink: boolean;
  onLink: (res: InviteResult) => void;
  onChanged: () => Promise<void>;
  onError: (e: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(row.name);
  const [kind, setKind] = useState<PartnerKind>(row.kind);
  const [busy, setBusy] = useState(false);
  const archived = row.status === "archived";
  const listed = row.status === "listed";
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  async function run<T extends { ok: boolean }>(
    label: string,
    fn: () => Promise<T>,
  ): Promise<T | null> {
    setBusy(true);
    try {
      const res = await fn();
      if (!res.ok && "error" in res) onError(String(res.error));
      return res;
    } catch (e) {
      console.error(`[contractors] ${label}`, e);
      onError(`Не удалось: ${label}`);
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    const res = await run("сохранить", () => updatePartnerFn({ data: { id: row.id, name, kind } }));
    if (res?.ok) {
      setEditing(false);
      await onChanged();
    }
  }

  async function reissue() {
    const res = await run("выдать ссылку", () => reissueInviteFn({ data: { id: row.id } }));
    if (res) {
      onLink(res);
      await onChanged();
    }
  }

  async function inviteFromList() {
    const res = await run("пригласить", () =>
      inviteListedFn({ data: { id: row.id, email: email.trim() } }),
    );
    if (res) {
      onLink(res);
      if (res.ok) {
        setInviting(false);
        setEmail("");
        await onChanged();
      }
    }
  }

  async function archive() {
    const res = await run("отправить в архив", () => archivePartnerFn({ data: { id: row.id } }));
    if (res?.ok) await onChanged();
  }

  async function restore() {
    const res = await run("вернуть из архива", () => restorePartnerFn({ data: { id: row.id } }));
    if (res?.ok) await onChanged();
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-3 px-3 py-2.5", archived && "opacity-60")}>
      {editing ? (
        <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-[1fr_170px]">
          <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9" />
          <KindSelect value={kind} onChange={setKind} className="h-9" />
        </div>
      ) : (
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/contractor/$id"
              params={{ id: row.id }}
              className="truncate text-sm font-medium hover:underline"
            >
              {row.name}
            </Link>
            <Badge variant="outline" className="font-normal">
              {KIND_LABEL[row.kind]}
            </Badge>
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {[row.email, row.telegram, statusText(row), reqText(row)].filter(Boolean).join(" · ")}
          </div>
          {listed && row.note && (
            <div className="mt-0.5 text-xs text-muted-foreground/80">{row.note}</div>
          )}
        </div>
      )}

      {editing ? (
        <>
          <Button size="sm" onClick={() => void save()} disabled={busy || !name.trim()}>
            Сохранить
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setEditing(false);
              setName(row.name);
              setKind(row.kind);
            }}
          >
            Отмена
          </Button>
        </>
      ) : listed && inviting ? (
        <>
          <Input
            type="email"
            value={email}
            placeholder="почта для входа"
            className="h-9 w-56"
            autoFocus
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && email.trim()) void inviteFromList();
            }}
          />
          <Button size="sm" onClick={() => void inviteFromList()} disabled={busy || !email.trim()}>
            Выдать ссылку
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setInviting(false)}>
            Отмена
          </Button>
        </>
      ) : listed ? (
        <>
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)} aria-label="Поправить">
            <Pencil className="size-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setInviting(true)} disabled={!canLink}>
            <UserPlus className="size-4" />
            Пригласить
          </Button>
        </>
      ) : archived ? (
        <Button size="sm" variant="ghost" onClick={() => void restore()} disabled={busy}>
          <RotateCcw className="size-4" />
          Вернуть
        </Button>
      ) : (
        <>
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)} aria-label="Поправить">
            <Pencil className="size-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void reissue()}
            disabled={busy || !canLink}
          >
            <Link2 className="size-4" />
            {row.hasAccount ? "Сбросить пароль" : "Новая ссылка"}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" aria-label={`В архив: ${row.name}`} disabled={busy}>
                <Archive className="size-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Отправить «{row.name}» в архив?</AlertDialogTitle>
                <AlertDialogDescription>
                  Вход в кабинет закроется сразу, ссылка-приглашение погаснет. Данные подрядчика
                  останутся, вернуть можно в любой момент.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Отмена</AlertDialogCancel>
                <AlertDialogAction onClick={() => void archive()}>В архив</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}

/**
 * Насколько заполнены обязательные реквизиты — Даша разбирает список по
 * этому: у кого всё есть, кого дозаполнить, о ком не знаем ничего.
 */
type Fill = "full" | "partial" | "empty";

const FILL_LABEL: Record<Fill, string> = {
  full: "Заполнено всё",
  partial: "Частично",
  empty: "Ничего",
};

function fillOf(row: PartnerRow): Fill {
  if (row.reqFilled >= row.reqTotal) return "full";
  return row.reqFilled > 0 ? "partial" : "empty";
}

/** Где реквизиты — то, ради чего Даша открывает список. */
function reqText(row: PartnerRow): string {
  if (row.reqState === "draft") return `реквизиты: ${row.reqFilled} из ${row.reqTotal}`;
  return `реквизиты: ${REQ_STATE_LABEL[row.reqState].toLowerCase()}`;
}

function statusText(row: PartnerRow): string {
  if (row.status === "archived") return STATUS_LABEL.archived;
  if (row.status === "listed") return "в списке, не приглашён";
  if (row.inviteLive && row.inviteExpiresAt) {
    const until = new Date(row.inviteExpiresAt).toLocaleDateString("ru-RU");
    return row.hasAccount ? `ссылка на новый пароль до ${until}` : `приглашён, ссылка до ${until}`;
  }
  if (row.status === "active") return STATUS_LABEL.active;
  return "ссылка истекла — выдайте новую";
}

/**
 * Импорт из таблицы Даши: она загружает свою «Табличку HR Comm» (xlsx),
 * видит, кого нашли, снимает лишние галочки и добавляет «в список». Почты
 * в таблице нет — её вписывают, когда приглашают.
 */
function ImportPanel({ onAdded }: { onAdded: () => Promise<void> }) {
  const input = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [kinds, setKinds] = useState<Record<string, PartnerKind>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load(file: File) {
    setBusy(true);
    setError(null);
    try {
      const buf = new Uint8Array(await file.arrayBuffer());
      let bin = "";
      for (let i = 0; i < buf.length; i += 8192)
        bin += String.fromCharCode(...buf.subarray(i, i + 8192));
      const res = await previewImport({ data: { base64: btoa(bin) } });
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setPreview(res.preview);
      setPicked(new Set(res.preview.candidates.filter((c) => !c.exists).map((c) => c.key)));
      setKinds({});
    } catch (e) {
      console.error("[import] не удалось прочитать таблицу", e);
      setError("Не удалось прочитать таблицу");
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  }

  async function add() {
    if (!preview) return;
    setBusy(true);
    try {
      const items = preview.candidates
        .filter((c) => picked.has(c.key))
        .map(({ exists: _exists, ...c }) => ({ ...c, kind: kinds[c.key] ?? c.kind }));
      const res = await applyImport({ data: { items } });
      toast.success(`Добавлено в список: ${res.added}`);
      setPreview(null);
      await onAdded();
    } catch (e) {
      console.error("[import] не удалось добавить", e);
      setError("Не удалось добавить подрядчиков");
    } finally {
      setBusy(false);
    }
  }

  const fresh = preview?.candidates.filter((c) => !c.exists) ?? [];
  const toggle = (key: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <FileSpreadsheet className="size-4 text-muted-foreground" />
          <span className="text-sm font-medium">Загрузить из таблицы</span>
          <span className="text-xs text-muted-foreground">
            «Табличка HR Comm: Документы», .xlsx — подрядчики встанут в список, приглашать по одному
          </span>
          <input
            ref={input}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void load(f);
            }}
          />
          <Button
            size="sm"
            variant="outline"
            className="ml-auto"
            disabled={busy}
            onClick={() => input.current?.click()}
          >
            {busy && !preview && <Loader2 className="size-4 animate-spin" />}
            Выбрать файл
          </Button>
        </div>

        {error && <p className="text-sm text-destructive-ink">{error}</p>}

        {preview && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {preview.sheets.join(", ")}: {preview.rows} строк, подрядчиков{" "}
              {preview.candidates.length}
              {preview.candidates.length > fresh.length &&
                `, из них уже заведены ${preview.candidates.length - fresh.length}`}
              . Строки с оплатой через ОФК пропущены — это не договоры.
            </p>
            <div className="max-h-96 divide-y overflow-y-auto rounded-md border">
              {fresh.map((c) => (
                <label key={c.key} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={picked.has(c.key)}
                    onChange={() => toggle(c.key)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="font-medium">{c.name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {[
                        c.telegram,
                        `${c.rows} стр.`,
                        c.executors.length ? `на другое лицо: ${c.executors.join(", ")}` : "",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                  <KindSelect
                    value={kinds[c.key] ?? c.kind}
                    onChange={(k) => setKinds((prev) => ({ ...prev, [c.key]: k }))}
                    className={cn(
                      "h-8 w-[150px]",
                      c.kindGuessed && !kinds[c.key] && "border-warning",
                    )}
                  />
                </label>
              ))}
            </div>
            {fresh.some((c) => c.kindGuessed) && (
              <p className="text-xs text-warning-ink">
                Оргформа с жёлтой рамкой угадана — в таблице её не было. Проверьте.
              </p>
            )}
            <div className="flex gap-2">
              <Button onClick={() => void add()} disabled={busy || picked.size === 0}>
                {busy && <Loader2 className="size-4 animate-spin" />}
                Добавить в список: {picked.size}
              </Button>
              <Button variant="ghost" onClick={() => setPreview(null)} disabled={busy}>
                Отмена
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
